import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { useTranslation } from 'next-i18next';

import { breakpoints } from '@themes/main';

import GridSectionContent from '@common/components/GridSectionContent';
import { MAX_ALTERNATIVES_COUNT } from '@common/constants/jobs';
import { SectionDescription, SectionTitle } from '../JobApplyForm.styled';

import {
  CountInfo,
  CountInfoWrapper,
  CountLabel,
  CountPill,
  BackgroundGradient,
  AlternativesList,
  AlternativesListWrapper,
  AlternativesSection,
  ShowAllButton,
  JobOffersItem,
  countStates,
} from './AlternativeJobsSection.styled';

const AlternativeJobsSection = ({
  handleAlternativeOfferClicked,
  alternativeOffers,
  otherJobOffers,
  isLast,
}) => {
  const { t } = useTranslation();
  const alternativesListRef = useRef(null);
  const alternativesListWrapperRef = useRef(null);
  const countRef = useRef(null);
  const countWrapperRef = useRef(null);
  const [isAlternativesListCollapsed, setIsAlternativesListCollapsed] =
    useState(true);
  const [alternativesListMaxHeight, setAlternativesListMaxHeight] =
    useState('100%');
  const [countState, setCountState] = useState(countStates.sitckyHidden);

  const hasMaxAlternatives = otherJobOffers.length >= MAX_ALTERNATIVES_COUNT;
  const alternativesLeft = MAX_ALTERNATIVES_COUNT - otherJobOffers.length;

  useEffect(() => {
    const handleSetAlternativesHeight = () => {
      let enoughItemsToCollapse;

      if (window.innerWidth > breakpoints[9] * 10) {
        enoughItemsToCollapse = alternativeOffers.length >= 7;
      } else if (window.innerWidth > breakpoints[12] * 10) {
        enoughItemsToCollapse = alternativeOffers.length >= 5;
      } else {
        enoughItemsToCollapse = alternativeOffers.length >= 3;
      }
      // We need to multiply breakpoint by 10 because they are given in rem's but we need px's.

      if (
        isAlternativesListCollapsed &&
        enoughItemsToCollapse &&
        alternativesListRef.current?.children?.length
      ) {
        setAlternativesListMaxHeight(
          `${alternativesListRef.current.children[0].offsetHeight * 1.4}px`,
        );
      } else {
        setAlternativesListMaxHeight('100%');
      }
    };

    const listResizeObserver = new ResizeObserver(
      debounce(handleSetAlternativesHeight, 500),
    );

    const alternativesListElement = alternativesListRef.current;

    listResizeObserver.observe(alternativesListElement);

    handleSetAlternativesHeight();

    return () => {
      listResizeObserver.unobserve(alternativesListElement);
    };
  }, [alternativeOffers.length, isAlternativesListCollapsed]);

  useEffect(() => {
    const handleCountPositioning = () => {
      if (!alternativesListWrapperRef.current && !countRef.current) return;

      const rect = alternativesListWrapperRef.current.getBoundingClientRect();
      const appearInViewPort =
        rect.top - window.innerHeight + countRef.current.offsetHeight < 0;
      const isFullyVisibleInViewPort = rect.bottom < window.innerHeight;

      if (appearInViewPort && !isFullyVisibleInViewPort && isLast) {
        setCountState(countStates.stickyVisible);
      } else if (!appearInViewPort && !isFullyVisibleInViewPort && isLast) {
        setCountState(countStates.stickyHidden);
      } else {
        setCountState(countStates.static);
      }
    };

    const alternativesListWrapper = alternativesListWrapperRef.current;
    const countWrapper = countWrapperRef.current;

    const listApearObserver = new IntersectionObserver(handleCountPositioning, {
      threshold: 0,
      rootMargin: `-${countRef.current.offsetHeight}px`,
    });
    const listFullyVisibleObserver = new IntersectionObserver(
      handleCountPositioning,
      {
        threshold: 1,
      },
    );

    listApearObserver.observe(alternativesListWrapper);
    listFullyVisibleObserver.observe(countWrapper);

    handleCountPositioning();

    return () => {
      listApearObserver.unobserve(alternativesListWrapper);
      listFullyVisibleObserver.unobserve(countWrapper);
    };
  }, [isAlternativesListCollapsed, isLast]);

  return (
    <AlternativesSection>
      <SectionTitle>
        {t('jobApplyForm.alternativesSection.title', 'Weitere Stellen')}
      </SectionTitle>
      <SectionDescription>
        {t(
          'jobApplyForm.alternativesSection.info',
          'Interessierst Du Dich noch für weitere ausgeschriebene Stellen? Falls ja, dann kannst Du hier bis zu vier weitere Stellen auswählen, für die Du Dich zusätzlich bewerben möchtest.',
        )}
      </SectionDescription>
      <AlternativesListWrapper ref={alternativesListWrapperRef}>
        <AlternativesList
          maxHeight={alternativesListMaxHeight}
          ref={alternativesListRef}
        >
          {alternativeOffers.map(offer => {
            const selected = otherJobOffers.includes(offer.id);

            return (
              <JobOffersItem
                selected={selected}
                forwardedAs="button"
                type="button"
                key={offer.id}
                onClick={handleAlternativeOfferClicked(offer.id)}
                disabled={hasMaxAlternatives && !selected}
                {...offer}
              />
            );
          })}
          {isAlternativesListCollapsed &&
            alternativesListMaxHeight !== '100%' && (
              <BackgroundGradient>
                <ShowAllButton
                  type="button"
                  onClick={() => setIsAlternativesListCollapsed(false)}
                >
                  {t(
                    'jobApplyForm.alternativesSection.showAllButton',
                    'Alle anzeigen',
                  )}
                </ShowAllButton>
              </BackgroundGradient>
            )}
        </AlternativesList>
        <CountInfoWrapper
          ref={countWrapperRef}
          height={countRef?.current?.offsetHeight || 0}
          isHidden={alternativeOffers.length < 4}
        >
          <CountInfo ref={countRef} state={countState}>
            <GridSectionContent
              columns={countState === countStates.static ? 12 : 8}
            >
              <CountLabel>
                {t(
                  'jobApplyForm.alternativesSection.selectionsLeft',
                  'Verbleibend',
                )}
              </CountLabel>
              <CountPill>{alternativesLeft}</CountPill>
            </GridSectionContent>
          </CountInfo>
        </CountInfoWrapper>
      </AlternativesListWrapper>
    </AlternativesSection>
  );
};

AlternativeJobsSection.propTypes = {
  alternativeOffers: PropTypes.arrayOf(PropTypes.shape({})),
  handleAlternativeOfferClicked: PropTypes.func.isRequired,
  otherJobOffers: PropTypes.arrayOf(PropTypes.number).isRequired,
  isLast: PropTypes.bool.isRequired,
};

AlternativeJobsSection.defaultProps = {
  alternativeOffers: [],
};

export default AlternativeJobsSection;
